// The clinic app's impersonation state (Frontend §6.4; D-40): the session id from the `#imp=` fragment, kept in
// `sessionStorage` (tab-scoped) under `pa.impersonation`, plus the clinic once `/v1/me` has confirmed it. The id is
// sent as `X-Impersonation-Id` only to `/v1/me`, the impersonated clinic's routes and #36, never in a URL.

export const IMPERSONATION_KEY = 'pa.impersonation'

export interface ImpersonationState {
  id: string
  /** The impersonated clinic, known after `/v1/me` answered with the session (for Exit and the header scope). */
  clinicId?: string
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** Storage that may be missing or throw (private mode, tests). */
export interface KeyValueStore {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

function store(): KeyValueStore | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage
  } catch {
    return null
  }
}

export function readImpersonation(s: KeyValueStore | null = store()): ImpersonationState | null {
  try {
    const raw = s?.getItem(IMPERSONATION_KEY)
    if (!raw) return null
    const value = JSON.parse(raw) as Partial<ImpersonationState>
    if (typeof value.id !== 'string' || !UUID.test(value.id)) return null
    return {
      id: value.id,
      ...(typeof value.clinicId === 'string' && UUID.test(value.clinicId) && { clinicId: value.clinicId }),
    }
  } catch {
    return null
  }
}

export function writeImpersonation(state: ImpersonationState, s: KeyValueStore | null = store()): void {
  try {
    s?.setItem(IMPERSONATION_KEY, JSON.stringify(state))
  } catch {
    // No storage: the session simply can't be resumed after a reload.
  }
}

export function clearImpersonation(s: KeyValueStore | null = store()): void {
  try {
    s?.removeItem(IMPERSONATION_KEY)
  } catch {
    // nothing to clear
  }
}

/** `#imp={id}` (the S-06 hand-off) → the id, or null for anything else. */
export function impersonationFromHash(hash: string): string | null {
  const params = new URLSearchParams(hash.replace(/^#/, ''))
  const id = params.get('imp')
  return id && UUID.test(id) ? id.toLowerCase() : null
}

/** The `#36` path: Exit and sign-out while impersonating (API.md §6.23). */
export const IMPERSONATION_END_PATH = '/v1/clinic/impersonation/end'

/**
 * `getImpersonationId(path)` (Frontend §3.2, §6.4 step 4): the id only for `/v1/me`, #36 and the impersonated
 * clinic's own routes (before `/v1/me` names the clinic, only `/v1/me` and #36 get it).
 */
export function impersonationIdFor(path: string, state: ImpersonationState | null): string | null {
  if (!state) return null
  if (path === '/v1/me' || path === IMPERSONATION_END_PATH) return state.id
  if (state.clinicId && path.startsWith(`/v1/clinic/clinics/${state.clinicId}/`)) return state.id
  return null
}

/** S-04 for the clinic, with the "Impersonation ended" notice (Frontend §6.4 steps 6–7). */
export const adminClinicHref = (adminOrigin: string, clinicId: string) =>
  `${adminOrigin}/clinics/${clinicId}/summary?notice=impersonation-ended`

/** Minutes left until `expiresAt` (rounded up, never below 0): the banner's "Ends in {N} min". */
export function minutesLeft(expiresAt: string, now: number): number {
  return Math.max(0, Math.ceil((Date.parse(expiresAt) - now) / 60_000))
}

/** A-01's hint while an impersonation waits for sign-in on this origin (Frontend §6.4 step 2; Firebase is per origin). */
export const IMPERSONATION_SIGN_IN_HINT = 'Sign in with your super admin account to continue'
